/**
 * Formata datas de chamados do portal (MySQL dateStrings).
 */

export function parsePortalDateTime(value) {
  if (value == null || value === "") return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatPortalRelativeTime(value) {
  const d = parsePortalDateTime(value);
  if (!d) return "";
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return "agora";
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "agora";
  if (mins < 60) return `há ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `há ${days} dia${days > 1 ? "s" : ""}`;
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

export function formatPortalDateTimeFull(value) {
  const d = parsePortalDateTime(value);
  if (!d) return "";
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Data/hora compacta para bolhas de mensagem (ex.: 15/06/2026, 23:24). */
export function formatPortalMessageDateTime(value) {
  const d = parsePortalDateTime(value);
  if (!d) return String(value || "").trim();
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatPortalMessageDateTimeNow(date = new Date()) {
  const d = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ticketDisplayTitle(ticket) {
  const type = String(ticket?.typeName || "").trim();
  if (type && !/^n[aã]o identificado$/i.test(type)) return type;
  return `Solicitação #${ticket?.id || "—"}`;
}

export function ticketPreviewText(ticket) {
  const preview = String(ticket?.lastMessagePreview || "").trim();
  if (!preview) return "Nenhuma mensagem ainda";
  return preview.length > 140 ? `${preview.slice(0, 137)}…` : preview;
}

/** Canceladas não entram no portal. */
export function isPortalListableTicket(ticket) {
  return Number(ticket?.status) !== 5;
}

export function isGenericPortalTypeName(typeName) {
  const type = String(typeName || "").trim();
  return !type || /^n[aã]o identificado$/i.test(type);
}

/**
 * Texto amigável do responsável pela solicitação (humano, chatbot ou agente IA).
 */
export function formatPortalAssigneeLabel(ticket) {
  const name = String(ticket?.assignedToName || "").trim();
  const kind = ticket?.assignedToKind;

  if (name && kind === "human") return name;
  if (name && kind === "chatbot") return name;
  if (name && kind === "agent") return `${name} (assistente IA)`;

  const status = Number(ticket?.status);
  if (status === 0) return "Aguardando um atendente";
  if (status === 1) return "Nossa equipe está com seu caso";
  if (isGenericPortalTypeName(ticket?.typeName)) return "Equipe de atendimento";

  return "A definir";
}

export function ticketHeaderSubject(ticket) {
  const type = String(ticket?.typeName || "").trim();
  if (type && !/^n[aã]o identificado$/i.test(type)) return type;

  const preview = String(ticket?.lastMessagePreview || ticket?.subject || "").trim();
  if (preview) return preview.length > 80 ? `${preview.slice(0, 77)}…` : preview;

  return ticket?.id ? `Protocolo ${ticket.id}` : "Sua solicitação";
}

/** Estilo do chip de responsável (somente leitura), alinhado ao chat. */
export function getPortalAssigneeChipStyles(ticket, theme) {
  const kind = ticket?.assignedToKind;

  if (kind === "agent") {
    return {
      bg: theme.palette.secondary.main,
      color: theme.palette.common.white,
    };
  }
  if (kind === "chatbot") {
    return {
      bg: theme.palette.warning?.main || "#ed6c02",
      color: theme.palette.common.white,
    };
  }
  if (kind === "human") {
    return {
      bg: theme.palette.info.main,
      color: theme.palette.common.white,
    };
  }

  return {
    bg: theme.palette.grey[700],
    color: theme.palette.common.white,
  };
}

export function getPortalAssigneeChipLabel(ticket) {
  const name = String(ticket?.assignedToName || "").trim();
  const kind = ticket?.assignedToKind;

  if (name && kind === "chatbot") {
    return name.toLowerCase().includes("chatbot") ? name : `${name} (ChatBot)`;
  }
  if (name && kind === "agent") {
    return name.toLowerCase().includes("ia") ? name : `${name} (IA)`;
  }
  if (name) return name;

  return formatPortalAssigneeLabel(ticket);
}

export function formatPortalAssigneeHint(ticket) {
  const kind = ticket?.assignedToKind;
  if (kind === "human") return "Atendente responsável";
  if (kind === "chatbot") return "Atendimento automatizado";
  if (kind === "agent") return "Assistente com IA";
  return "Responsável";
}
